import { ProductInfo, ProductOptions } from '@storefront/hooks/useProductsInfo';

import { splitSKU } from './modal-printer-dynamic-comparison.features';
import {
  ModalPrinterComparisonAnalyticsPayload,
  PrinterFeaturesList,
} from '@monetization/hpaip-components-library';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import DataCollection from '@storefront/services/DataCollection';

export const MAX_SCREEN_ELEMENTS = 3;
export const MIN_SCREEN_ELEMENTS_SIZE = 2;

export const sortProductsByOrder = (
  productsOptions: ProductOptions[],
  productOrder: string[]
): ProductOptions[] => {
  const normalizeLabel = (label?: string) => (label || '').toLowerCase();

  const sortByProductOrder = (a: ProductOptions, b: ProductOptions) => {
    const indexA = productOrder.findIndex(
      (order) => normalizeLabel(a.label) === order.toLowerCase()
    );
    const indexB = productOrder.findIndex(
      (order) => normalizeLabel(b.label) === order.toLowerCase()
    );
    return indexA - indexB;
  };

  return [...productsOptions].sort(sortByProductOrder);
};

export const productsOptionSmallScreen = (
  productsOptions: ProductOptions[],
  activeSKU?: string
) => {
  let productOrder: string[] = [
    'HP Envy Photo',
    'HP Smart Tank',
    'HP OfficeJet Pro',
    'HP Envy',
  ];
  let productOrderOriginal = [
    'HP Envy',
    'HP Envy Photo',
    'HP OfficeJet Pro',
    'HP Smart Tank',
  ];

  if (activeSKU) {
    const activeLabel =
      productsOptions.find((option) => option.value === activeSKU)?.label || '';
    const activeIndex = productOrderOriginal.findIndex((order) =>
      order.toLowerCase().includes(activeLabel.toLowerCase())
    );
    if (activeIndex === 3) {
      const reversedOrder = [...productOrderOriginal].reverse();
      productOrder = reversedOrder;
    } else {
      const nextLabel = productOrderOriginal[activeIndex + 1] || '';
      const productOrderLeft = productOrderOriginal.filter(
        (order) => order !== activeLabel && order !== nextLabel
      );
      productOrder = [activeLabel, nextLabel, ...productOrderLeft];
    }
  }

  const sortedProductsOptions = sortProductsByOrder(
    productsOptions,
    productOrder
  );
  const productsOptionsMobile = sortedProductsOptions;

  return productsOptionsMobile;
};

export const findPrinterBySku = (
  selectedPrinter: string,
  productsInfo: ProductInfo[]
) => productsInfo.find((printer) => printer.sku === selectedPrinter);

export const findFeatureBySku = (
  printerColumn: number,
  featuresList: [] | PrinterFeaturesList[],
  selectedPrefixArray: string[]
) =>
  featuresList.find(
    (printer) =>
      splitSKU(printer ? printer.id : '') === selectedPrefixArray[printerColumn]
  );

export const getShouldRenderSelect = (
  columnNumber: number,
  productsOptions: ProductOptions[]
) => productsOptions.length > columnNumber;

export const handleFullSpecsAnalytics = (
  data: ModalPrinterComparisonAnalyticsPayload
) => {
  DataCollection.sendEvent({
    ...uiEvents.fullSpecsGenericHLC,
    ...data,
  });
};

export function getResponsiveColumnCount(productsOptionsLength: number) {
  if (window.matchMedia('(max-width: 992px)').matches)
    return MIN_SCREEN_ELEMENTS_SIZE;
  else if (productsOptionsLength >= MAX_SCREEN_ELEMENTS)
    return MAX_SCREEN_ELEMENTS;
  else return productsOptionsLength;
}

export const getInitialSelection = (
  activeSKU: string,
  productsOptions: ProductOptions[],
  columnsNumber: number
) => {
  let initialState: string[];
  if (!activeSKU) {
    initialState = [
      productsOptions[0]?.value || '',
      productsOptions[1]?.value || '',
      productsOptions[2]?.value || '',
    ];
  } else {
    const selectedProduct = productsOptions.find(
      (product) => product.value === activeSKU
    );
    const notSelectedProducts = productsOptions.filter(
      (product) => product.value !== activeSKU
    );
    initialState = [
      selectedProduct?.value,
      notSelectedProducts[0]?.value || '',
      notSelectedProducts[1]?.value || '',
    ];
  }
  if (columnsNumber === MIN_SCREEN_ELEMENTS_SIZE)
    return initialState.slice(0, 2);
  else return initialState;
};

export const getSelectedPrefixArray = (selectedArray: string[]) =>
  selectedArray.map((item: string) => item?.split('.')[0]);

export const getAvailableOptionsForColumn = (
  printerColumn: number,
  productsOptions: ProductOptions[],
  selectedArray: string[]
) =>
  productsOptions.filter(
    (printer) =>
      !selectedArray.includes(printer.value) ||
      printer.value === selectedArray[printerColumn]
  );

export const getFilteredOptions = (
  selectedArray: string[],
  currentColumnSku: string,
  productsOptions: ProductOptions[]
) =>
  productsOptions.filter(
    (printer) =>
      !selectedArray.includes(printer.value) ||
      printer.value === currentColumnSku
  );

export const calculateOptions = (
  selectedArray: string[],
  productsOptions: ProductOptions[]
) => {
  const newOptions = selectedArray.map((sku) =>
    getFilteredOptions(selectedArray, sku, productsOptions)
  );
  return newOptions;
};
